import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Phone, MessageCircle, CalendarDays, Users, BedDouble, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ROOMS, HOTEL, formatApiErrorDetail, inr } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toISO = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};
export const TODAY = toISO(new Date());
export const DEFAULT_CHECKOUT = toISO(new Date(Date.now() + 2 * 86400000));
export const defaultSelection = () => ({ check_in: TODAY, check_out: DEFAULT_CHECKOUT, guests: 2, room_type: "deluxe" });

const fieldLabel = "mb-2 block text-[10px] font-medium uppercase tracking-[0.28em] text-[#8C713B]";
const fieldInput =
  "w-full border-b border-[#2A2421] bg-transparent py-2 text-sm text-[#F5F2EB] outline-none transition-colors focus:border-[#C5A059]";

export function BookingBar({ onSearch }) {
  const [form, setForm] = useState(defaultSelection());

  const submit = (e) => {
    e.preventDefault();
    if (!form.check_in || !form.check_out) return toast.error("Please select your dates");
    if (form.check_out <= form.check_in) return toast.error("Check-out must be after check-in");
    onSearch({ ...form });
  };

  return (
    <form
      data-testid="booking-search-form"
      onSubmit={submit}
      className="grid grid-cols-1 gap-6 border border-[#2A2421] bg-[#161311]/90 p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl sm:grid-cols-2 lg:grid-cols-5 sm:p-8"
    >
      <div>
        <label className={fieldLabel} htmlFor="checkin">
          <CalendarDays size={12} className="mr-1 inline" /> Check-in
        </label>
        <input
          id="checkin"
          data-testid="booking-checkin-input"
          type="date"
          min={TODAY}
          value={form.check_in}
          onChange={(e) => setForm({ ...form, check_in: e.target.value })}
          className={fieldInput}
        />
      </div>
      <div>
        <label className={fieldLabel} htmlFor="checkout">
          <CalendarDays size={12} className="mr-1 inline" /> Check-out
        </label>
        <input
          id="checkout"
          data-testid="booking-checkout-input"
          type="date"
          min={form.check_in || TODAY}
          value={form.check_out}
          onChange={(e) => setForm({ ...form, check_out: e.target.value })}
          className={fieldInput}
        />
      </div>
      <div>
        <label className={fieldLabel} htmlFor="guests">
          <Users size={12} className="mr-1 inline" /> Guests
        </label>
        <select
          id="guests"
          data-testid="booking-guests-select"
          value={form.guests}
          onChange={(e) => setForm({ ...form, guests: Number(e.target.value) })}
          className={`${fieldInput} cursor-pointer`}
        >
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <option key={n} value={n} className="bg-[#161311]">
              {n} {n === 1 ? "Guest" : "Guests"}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={fieldLabel} htmlFor="roomtype">
          <BedDouble size={12} className="mr-1 inline" /> Room
        </label>
        <select
          id="roomtype"
          data-testid="booking-room-select"
          value={form.room_type}
          onChange={(e) => setForm({ ...form, room_type: e.target.value })}
          className={`${fieldInput} cursor-pointer`}
        >
          {ROOMS.map((r) => (
            <option key={r.id} value={r.id} className="bg-[#161311]">
              {r.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-end sm:col-span-2 lg:col-span-1">
        <button
          data-testid="booking-search-submit"
          type="submit"
          className="w-full border border-[#C5A059] bg-[#C5A059] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0D0B0A] transition-all duration-300 hover:bg-transparent hover:text-[#C5A059]"
        >
          Check Availability
        </button>
      </div>
    </form>
  );
}

export function EnquiryModal({ selection, onClose }) {
  const room = ROOMS.find((r) => r.id === selection.room_type) || ROOMS[0];
  const nights = useMemo(
    () => Math.max(1, Math.round((new Date(selection.check_out) - new Date(selection.check_in)) / 86400000)),
    [selection]
  );
  const total = nights * room.price;
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [state, setState] = useState("form");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        notes: form.notes || null,
        room_type: room.id,
        check_in: selection.check_in,
        check_out: selection.check_out,
        guests: Number(selection.guests),
      });
      setState("done");
    } catch (err) {
      setState("form");
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your enquiry. Please call us instead.");
    }
  };

  const waText = encodeURIComponent(
    `Hello The Rayan, I am ${form.name}. I would like to book the ${room.name} from ${selection.check_in} to ${selection.check_out} for ${selection.guests} guest(s).`
  );

  return (
    <motion.div
      data-testid="enquiry-modal"
      className="fixed inset-0 z-[70] flex items-end justify-center bg-[#0D0B0A]/80 backdrop-blur-sm sm:items-center sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-[#2A2421] bg-[#161311] p-7 sm:p-9"
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Reservation Enquiry</p>
            <h3 className="mt-2 font-display text-2xl text-[#F5F2EB]">{room.name}</h3>
          </div>
          <button data-testid="enquiry-close-button" onClick={onClose} className="text-[#A8A29E] transition-colors hover:text-[#C5A059]" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="mb-6 grid grid-cols-3 gap-3 border border-[#2A2421] bg-[#0D0B0A]/60 p-4 text-center">
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#8C713B]">Check-in</p>
            <p className="mt-1 text-xs text-[#F5F2EB]" data-testid="enquiry-checkin-summary">{selection.check_in}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#8C713B]">Nights</p>
            <p className="mt-1 text-xs text-[#F5F2EB]" data-testid="enquiry-nights-summary">{nights}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#8C713B]">Estimate</p>
            <p className="mt-1 text-xs text-[#C5A059]" data-testid="enquiry-total-summary">{inr(total)}</p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {state === "done" ? (
            <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="enquiry-success">
              <CheckCircle2 size={44} className="mx-auto text-[#C5A059]" />
              <h4 className="mt-4 text-center font-display text-2xl text-[#F5F2EB]">Enquiry Received</h4>
              <p className="mt-3 text-center text-sm leading-relaxed text-[#A8A29E]">
                Thank you, {form.name.split(" ")[0]}. Our front desk will call you shortly to confirm availability.
                For an instant confirmation, reach us directly:
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <a
                  data-testid="enquiry-whatsapp-button"
                  href={`${HOTEL.whatsapp}?text=${waText}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 border border-[#C5A059] bg-[#C5A059] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0D0B0A] transition-all hover:bg-transparent hover:text-[#C5A059]"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                  data-testid="enquiry-call-button"
                  href={HOTEL.phoneLink}
                  className="flex items-center justify-center gap-2 border border-[#2A2421] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F5F2EB] transition-all hover:border-[#C5A059] hover:text-[#C5A059]"
                >
                  <Phone size={14} /> Call Us
                </a>
              </div>
            </motion.div>
          ) : (
            <motion.form key="form" onSubmit={submit} className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div>
                <label className={fieldLabel} htmlFor="enq-name">Full Name</label>
                <input
                  id="enq-name"
                  data-testid="enquiry-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={fieldInput}
                  placeholder="Your name"
                />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className={fieldLabel} htmlFor="enq-phone">Phone</label>
                  <input
                    id="enq-phone"
                    data-testid="enquiry-phone-input"
                    required
                    type="tel"
                    minLength={8}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={fieldInput}
                    placeholder="+91 ..."
                  />
                </div>
                <div>
                  <label className={fieldLabel} htmlFor="enq-email">Email (optional)</label>
                  <input
                    id="enq-email"
                    data-testid="enquiry-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={fieldInput}
                    placeholder="you@email.com"
                  />
                </div>
              </div>
              <div>
                <label className={fieldLabel} htmlFor="enq-notes">Special Requests</label>
                <textarea
                  id="enq-notes"
                  data-testid="enquiry-notes-input"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className={`${fieldInput} resize-none`}
                  placeholder="Airport pickup, early check-in, celebrations..."
                />
              </div>
              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={state === "sending"}
                className="flex w-full items-center justify-center gap-2 border border-[#C5A059] bg-[#C5A059] px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0D0B0A] transition-all duration-300 hover:bg-transparent hover:text-[#C5A059] disabled:opacity-60"
              >
                {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : null}
                {state === "sending" ? "Sending..." : "Send Enquiry"}
              </button>
              <p className="text-center text-[10px] tracking-wide text-[#78716C]">
                No payment now — our team confirms every stay personally.
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
